import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { AudioContentApi } from '../../services/api/AudioContentApi';
import SidebarItemMenu from './SidebarItemMenu';
import ProcessingSection from './ProcessingSection';
import { useGenerationJobs } from '../../hooks/useGenerationJobs';
import './SidebarItemMenu.css';
import './AudioLibraryPanel.css';

const AUDIO_PIN_KEY_PREFIX = 'audioLibrary.pinnedIds.';

const AudioLibraryPanel = ({ 
  format, // "podcast" or "audiobook"
  onSelectItem, // Callback when user selects item from list
  onNewItem, // Callback when user clicks "New" button
  showNewButton = true,
  hideHeader = false,
  hideSearch = false,
  showSectionHeader = false,
  onToggleCollapse,
  sidebarCollapsed,
  activeItemId,
  externalSearchQuery
}) => {
  const [audioLibrary, setAudioLibrary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const { listAudioContentSWR } = useData();
  const { addToast } = useToast();
  const { processingJobs, failedJobs, dismissJob: dismissGenJob } = useGenerationJobs(format);

  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const pinKey = AUDIO_PIN_KEY_PREFIX + format;
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(pinKey)) || []; } catch { return []; }
  });

  const togglePin = useCallback((id) => {
    setPinnedIds(prev => {
      const next = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      try { localStorage.setItem(pinKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [pinKey]);

  const handleRename = useCallback((id, currentTitle) => {
    setRenaming(id);
    setRenameValue(currentTitle || '');
  }, []);

  const handleRenameSubmit = useCallback(async (id) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenaming(null); return; }
    try {
      await AudioContentApi.updateAudioContent(id, { title: trimmed });
      setAudioLibrary(prev => prev.map(item => item.id === id ? { ...item, title: trimmed } : item));
      addToast({ intent: 'success', message: `${format === 'podcast' ? 'Podcast' : 'Audiobook'} renamed` });
    } catch (err) {
      console.error('Failed to rename audio:', err);
      addToast({ intent: 'danger', message: 'Failed to rename' });
    }
    setRenaming(null);
    setRenameValue('');
  }, [renameValue, format, addToast]);

  const handleDeleteAudio = useCallback(async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${format}?`)) return;
    try {
      await AudioContentApi.deleteAudioContent(id);
      setAudioLibrary(prev => prev.filter(item => item.id !== id));
      addToast({ intent: 'success', message: `${format === 'podcast' ? 'Podcast' : 'Audiobook'} deleted` });
    } catch (err) {
      console.error('Failed to delete audio:', err);
      addToast({ intent: 'danger', message: 'Failed to delete' });
    }
  }, [format, addToast]);

  const formatLabel = format === 'podcast' ? 'Podcast' : 'Audiobook';
  const formatIcon = format === 'podcast' ? '🎙️' : '📖';

  // Extract array from API response shape
  const extractArray = useCallback((response) => {
    return response?.audioContents || response?.content || [];
  }, []);

  useEffect(() => {
    loadLibrary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format]); // Reload when format changes

  // Listen for audio creation events to refresh the list
  useEffect(() => {
    const handleAudioCreated = (e) => {
      // Only refresh if the event matches our format (or no format specified)
      if (!e.detail?.format || e.detail.format === format) {
        loadLibrary();
      }
    };
    window.addEventListener('audio:created', handleAudioCreated);
    return () => window.removeEventListener('audio:created', handleAudioCreated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format]);

  // Listen for playback progress events from WaveformPlayer
  useEffect(() => {
    const handleProgress = (e) => setPlaybackProgress(e.detail.progress);
    const handleStop = () => setPlaybackProgress(0);
    window.addEventListener('audio:progress', handleProgress);
    window.addEventListener('audio:progress-stop', handleStop);
    return () => {
      window.removeEventListener('audio:progress', handleProgress);
      window.removeEventListener('audio:progress-stop', handleStop);
    };
  }, []);

  const loadLibrary = async () => {
    setIsLoading(true);
    try {
      // Use SWR: returns cached data instantly, refreshes in background
      const response = await listAudioContentSWR(
        { format, size: 100 },
        // onUpdate: silently replace list when fresh data arrives
        (freshData) => {
          const freshArray = extractArray(freshData);
          setAudioLibrary(freshArray);
        }
      );
      // API returns {audioContents: [...], count: N} or {content: [...]}
      const audioArray = extractArray(response);
      setAudioLibrary(audioArray);
    } catch (error) {
      console.error(`Failed to load ${format} library:`, error);
      setAudioLibrary([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isPlayableAudioUrl = (url) => { // eslint-disable-line no-unused-vars
    return url && typeof url === 'string' && url.length > 10;
  };

  const effectiveSearchQuery = externalSearchQuery != null ? externalSearchQuery : searchQuery;
  const filteredItems = audioLibrary
    .filter(item => {
      const matches = !item.format || item.format === format;
      return matches;
    })
    .filter(item => 
      item.title?.toLowerCase().includes(effectiveSearchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const ap = pinnedIds.includes(a.id) ? 0 : 1;
      const bp = pinnedIds.includes(b.id) ? 0 : 1;
      return ap - bp;
    });

  if (isLoading) {
    return (
      <div className="audio-library-panel">
        {!hideHeader && (
          <div className="audio-library-panel__header">
            <h3 className="audio-library-panel__title">
              {formatIcon} My {formatLabel}s
            </h3>
          </div>
        )}
        <div className="audio-library-panel__loading">
          <div className="spinner"></div>
          <p>Loading {format}s...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-library-panel">
      {!hideHeader && (
        <div className="audio-library-panel__header">
          <div className="audio-library-panel__header-row">
            {onToggleCollapse && (
              <button
                type="button"
                className="audio-library-panel__collapse-btn"
                onClick={onToggleCollapse}
                title="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M9 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            
            {!hideSearch && (
              <div className="audio-library-panel__search">
                <input
                  type="text"
                  placeholder={`Search ${format}s...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="audio-library-panel__search-input"
                />
              </div>
            )}
            
            {showNewButton && onNewItem && (
              <button 
                className="audio-library-panel__new-btn"
                onClick={onNewItem}
                title={`Create New ${formatLabel}`}
              >
                +
              </button>
            )}
          </div>
        </div>
      )}

      {showSectionHeader && (
        <div className="audio-library-section-header">
          <span className="audio-library-section-title">{formatLabel.toUpperCase()} HISTORY</span>
          <span className="audio-library-count-pill">{filteredItems.length}</span>
        </div>
      )}

      <div className="audio-library-panel__list">
        <ProcessingSection
          processingJobs={processingJobs}
          failedJobs={failedJobs}
          onDismiss={dismissGenJob}
        />
        {filteredItems.length === 0 && processingJobs.length === 0 && failedJobs.length === 0 && audioLibrary.filter(item => !item.format || item.format === format).length === 0 ? (
          <div className="audio-library-panel__empty">
            <div className={`audio-library-panel__tutorial audio-library-panel__tutorial--${format}`}>
              <div className={`audio-library-panel__tutorial-badge audio-library-panel__tutorial-badge--${format}`}>
                {format === 'podcast' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                )}
              </div>
              <h4 className="audio-library-panel__tutorial-title">
                {format === 'podcast' ? 'Create Your First Podcast' : 'Create Your First Audiobook'}
              </h4>
              <p className="audio-library-panel__tutorial-text">
                {format === 'podcast' 
                  ? "Let Scout turn your course materials into a podcast-style episode you can listen to anywhere."
                  : "Transform your readings and notes into a longer-form audio experience built for deeper listening."}
              </p>
              <p className="audio-library-panel__tutorial-hint">
                {format === 'podcast'
                  ? 'Perfect for walks, commutes, and workouts'
                  : 'Best for dense readings and longer documents'}
              </p>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="audio-library-panel__empty">
            <p>No {format}s match your search.</p>
          </div>
        ) : (
          filteredItems.map((item) => {
            const itemId = item.id;
            const isPinned = pinnedIds.includes(itemId);
            const dateLabel = (item.created_at || item.createdAt) 
              ? formatRelativeTime(item.created_at || item.createdAt) 
              : '';
            const durationLabel = (item.durationSeconds || item.duration_seconds)
              ? formatDuration(item.durationSeconds || item.duration_seconds)
              : '';

            return (
              <div
                key={itemId}
                className={`pc-card ${activeItemId === itemId ? 'is-active' : ''} ${isPinned ? 'is-pinned' : ''}`}
                onClick={() => { if (renaming !== itemId) onSelectItem && onSelectItem(item); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectItem && onSelectItem(item); } }}
              >
                <div className="pc-left">
                  {isPinned && (
                    <span className="pc-pin-icon">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><path d="M12 17v5"/><path d="M9 2h6l-1 7h4l-5 8h-2l-5-8h4Z"/></svg>
                    </span>
                  )}
                  <div className="pc-title" title={item.title || `Untitled ${formatLabel}`}>
                    {renaming === itemId ? (
                      <input
                        className="sim-rename-input"
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => handleRenameSubmit(itemId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameSubmit(itemId);
                          else if (e.key === 'Escape') { setRenaming(null); setRenameValue(''); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      item.title || `Untitled ${formatLabel}`
                    )}
                  </div>
                  <div className="pc-sub">
                    {dateLabel && <span>{dateLabel}</span>}
                    {dateLabel && durationLabel && <span className="pc-dot">•</span>}
                    {durationLabel && <span>{durationLabel}</span>}
                  </div>
                </div>
                <SidebarItemMenu
                  onRename={() => handleRename(itemId, item.title)}
                  onPin={() => togglePin(itemId)}
                  isPinned={isPinned}
                  onDelete={() => handleDeleteAudio(itemId)}
                />
                {activeItemId === itemId && playbackProgress > 0 && (
                  <div className="pc-progress-bar">
                    <div className="pc-progress-fill" style={{ width: `${playbackProgress * 100}%` }} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AudioLibraryPanel;
